/**
 * The pieces the lab administration layout is built from.
 *
 * Separate from `LabShared` on purpose: that file holds the primitives every lab screen uses,
 * including the read-only operations views. This one holds the ones that only make sense while
 * authoring - the blast-radius badge, the row menu, the slide-over, the sticky save bar - and
 * keeping them apart stops an operations panel from quietly growing an admin affordance.
 *
 * They are all here rather than each with its first caller because the whole point is that the
 * four library views and the builder behave identically. A row menu that opens on click in one
 * place and on hover in another is the kind of difference nobody reports and everybody trips on.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  HbrDrawer,
  HbrDropdown,
  HbrIcon,
  HbrInput,
  HbrMenu,
  HbrMenuItem,
  HbrStickyActionBar,
} from '@harbor/elements-react-standalone';
import {
  caretDown,
  caretRight,
  dotsSix,
  dotsThreeVertical,
  magnifyingGlass,
  treeView,
  x,
} from '@harbor/elements/icons';
import toast, { Toaster } from 'react-hot-toast';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { LabOption } from '../../support/labService';

/* ------------------------------------------------------------------------- *
 * Grouped form sections
 * ------------------------------------------------------------------------- */

/**
 * One labelled group of fields, with a rule above it.
 *
 * The configuration item editor is the reason this exists: it has seven decisions on it that read
 * as one wall of inputs, and three of them (device, action, method) are the ones without which
 * nothing runs. Grouping them under TARGET / ACTION / VERIFICATION / BEHAVIOR is what makes the
 * form answerable in the order somebody actually thinks in, rather than in field order.
 *
 * `hint` is for the sentence that applies to the whole group rather than to one field - the
 * per-field help text is still where a field's own caveats belong.
 */
export const LabSection: React.FC<{
  title: string;
  hint?: React.ReactNode;
  /** Shown at the right of the header. For a "+ New ..." that creates what the group selects. */
  actions?: React.ReactNode;
  /** Starts closed. Used for the groups that are genuinely optional, never for a required one. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, hint, actions, collapsible = false, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const shown = collapsible ? open : true;

  return (
    <section className="border-t border-gray-200 pt-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          {collapsible && (
            <button
              type="button"
              className="flex items-center border-0 bg-transparent p-0 text-gray-500"
              aria-expanded={shown}
              onClick={() => setOpen(!open)}
            >
              <HbrIcon asset={shown ? caretDown : caretRight} className="text-base" />
            </button>
          )}
          <h4 className="m-0 text-xs font-semibold uppercase tracking-wider text-gray-500">
            {title}
          </h4>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>

      {hint && <p className="m-0 mb-3 text-xs text-gray-500">{hint}</p>}

      {shown && <div className="flex flex-col gap-4">{children}</div>}
    </section>
  );
};

/* ------------------------------------------------------------------------- *
 * Status
 * ------------------------------------------------------------------------- */

/**
 * How an authored row looks from here.
 *
 * Deliberately not run status. This screen is authoring, and it does not know whether the last
 * delivery passed - it knows whether what has been written can work at all. `healthy` therefore
 * means "nothing on this row will silently do the wrong thing", not "this passed", and the legend
 * says so out loud, because green read as "tested" would be worse than no dot.
 */
export type LabAuthoringStatus = 'untested' | 'healthy' | 'failing' | 'running' | 'retired';

const STATUS_STYLE: Record<LabAuthoringStatus, { colour: string; label: string }> = {
  untested: { colour: 'bg-gray-400', label: 'Nothing has been compiled from this yet' },
  healthy: { colour: 'bg-green-600', label: 'Complete - nothing here fails silently' },
  failing: { colour: 'bg-red-600', label: 'Will not do what it looks like it does' },
  running: { colour: 'bg-amber-500', label: 'Part-way there - see the warnings on the row' },
  retired: { colour: 'bg-gray-300', label: 'Disabled: still attached, compiles to nothing' },
};

/** The dot, and the sentence behind it. The sentence is the point; the dot is the shorthand. */
export const LabStatusDot: React.FC<{
  status: LabAuthoringStatus;
  /** Replaces the default sentence when the row can say something more specific. */
  title?: string;
  className?: string;
}> = ({ status, title, className = '' }) => {
  const style = STATUS_STYLE[status];

  return (
    <span
      className={`inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full ${style.colour} ${className}`}
      title={title || style.label}
      aria-label={title || style.label}
      role="img"
    />
  );
};

/** Spelled out once per screen, because four colours with no key is a puzzle, not a signal. */
export const LabStatusLegend: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 ${className}`}>
    <span className="font-medium">Dots report what is authored, not the last run:</span>
    {(Object.keys(STATUS_STYLE) as LabAuthoringStatus[]).map((status) => (
      <span className="flex items-center gap-1.5" key={status}>
        <LabStatusDot status={status} />
        {STATUS_STYLE[status].label}
      </span>
    ))}
  </div>
);

/* ------------------------------------------------------------------------- *
 * Usage - the blast radius
 * ------------------------------------------------------------------------- */

/** One place a library row is used, and where to go to see it. */
export type LabUsage = {
  /** Stable across renders. Prefixed by kind, since a lab and a scenario can share an id. */
  key: string;
  label: string;
  /** The tier this usage is at, so a mixed list reads correctly. */
  kind: string;
  hint?: string;
  /** Called when the usage is clicked. Omit and the entry is text rather than a link. */
  onOpen?: () => void;
  /** Something about this usage that makes it not count - a disabled link, a disabled parent. */
  inactive?: boolean;
};

/**
 * How many places this row is used, and which ones.
 *
 * The single most load-bearing number in the library views. Every one of these tables edits rows
 * that are shared by design - a configuration item is attached by many scenarios, a scenario is a
 * section of many labs - so an edit is never local, and the count is the only thing on the row
 * that says how far it reaches. A count with no way to see the list is a warning nobody can act
 * on, so it opens one.
 */
export const LabUsageBadge: React.FC<{
  usages: LabUsage[];
  /** What is being edited, for the modal title. */
  subject: string;
  /** Shown when there are none. "Not attached anywhere" is a different fact from "0". */
  emptyLabel?: string;
}> = ({ usages, subject, emptyLabel = 'Nowhere' }) => {
  const [open, setOpen] = useState(false);
  const active = usages.filter((usage) => !usage.inactive);

  if (!usages.length) {
    return <span className="text-xs text-gray-500">{emptyLabel}</span>;
  }

  return (
    <>
      <button
        type="button"
        className="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-700 hover:border-blue-500 hover:text-blue-700"
        onClick={() => setOpen(true)}
      >
        {usages.length}
        {active.length !== usages.length && (
          <span className="ml-1 text-gray-500">({active.length} live)</span>
        )}
      </button>

      <StbModal
        open={open}
        role="information"
        title={`Where ${subject} is used`}
        size="medium"
        scrollable
        submitLabel="Close"
        onClose={async () => setOpen(false)}
        onSubmit={async () => setOpen(false)}
      >
        <p className="m-0 mb-3 text-sm text-gray-600">
          Editing {subject} reaches every one of these. The ones marked as not counting are still
          attached - they are switched off at one end or the other, so they deliver nothing until
          that is changed back.
        </p>

        <ul className="m-0 list-none p-0">
          {usages.map((usage) => (
            <li key={usage.key} className="border-b border-gray-100 py-2 last:border-b-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="text-xs uppercase tracking-wide text-gray-400">
                    {usage.kind}
                  </span>{' '}
                  {usage.onOpen ? (
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0 text-sm font-medium text-blue-700 underline"
                      onClick={() => {
                        setOpen(false);
                        usage.onOpen?.();
                      }}
                    >
                      {usage.label}
                    </button>
                  ) : (
                    <span className="text-sm font-medium">{usage.label}</span>
                  )}
                  {usage.hint && (
                    <span className="block text-xs text-gray-500">{usage.hint}</span>
                  )}
                </div>
                {usage.inactive && (
                  <span className="text-xs text-amber-800">Does not count right now</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </StbModal>
    </>
  );
};

/* ------------------------------------------------------------------------- *
 * Row menu
 * ------------------------------------------------------------------------- */

export type LabRowAction = {
  key: string;
  label: string;
  onSelect: () => void;
  /** Icon asset from `@harbor/elements/icons`. */
  asset?: string | (() => string);
  /** Set for delete and detach, so the destructive entries do not read like the rest. */
  destructive?: boolean;
  disabled?: boolean;
  /** Why it is disabled. Worth more than a greyed-out row with no explanation. */
  disabledReason?: string;
};

/**
 * The ⋮ at the end of a row.
 *
 * One menu rather than a row of buttons because the actions are the same everywhere and there are
 * four to six of them: rendered inline they crowd out the columns that say what the row is, and
 * they push Delete next to Edit, which is where accidents live.
 *
 * A disabled entry stays in the menu rather than disappearing. "Delete is not available because
 * three labs use this" is the answer to the question somebody opened the menu to ask; a menu that
 * is simply missing Delete looks like a bug.
 */
export const LabRowMenu: React.FC<{ actions: LabRowAction[]; label?: string }> = ({
  actions,
  label = 'Row actions',
}) => {
  if (!actions.length) return null;

  return (
    <HbrDropdown onClick={(evt: React.MouseEvent) => evt.stopPropagation()}>
      <HbrIcon
        asset={dotsThreeVertical}
        slot="trigger"
        label={label}
        className="cursor-pointer p-1 text-xl opacity-50 hover:opacity-100"
      />
      <HbrMenu>
        {actions.map((action) => (
          <HbrMenuItem
            key={action.key}
            value={action.key}
            disabled={action.disabled}
            sentiment={action.destructive ? 'danger' : undefined}
            onClick={(evt: React.MouseEvent) => {
              evt.stopPropagation();
              if (!action.disabled) action.onSelect();
            }}
          >
            {action.asset && <HbrIcon asset={action.asset} slot="prefix" />}
            {action.label}
            {action.disabled && action.disabledReason && (
              <span slot="suffix" className="text-xs text-gray-500">
                {action.disabledReason}
              </span>
            )}
          </HbrMenuItem>
        ))}
      </HbrMenu>
    </HbrDropdown>
  );
};

/* ------------------------------------------------------------------------- *
 * Slide-over
 * ------------------------------------------------------------------------- */

/** Where a newly created row goes. The default is "attach", because that is why it was created. */
export type LabSaveDestination = 'library' | 'attach';

/**
 * A form that opens beside what you were doing instead of replacing it.
 *
 * Creating from inside the builder is the case this exists for. Navigating to a full-page form
 * loses the tree: which lab, which section, how far down, what was expanded. Coming back lands at
 * the top of a collapsed tree, and the admin has to find their place again to do the next of the
 * six things they came to do.
 *
 * The destination toggle at the bottom is not a preference - it is the difference between adding
 * to the catalogue and adding to *this* scenario, and getting it wrong in either direction is
 * invisible until much later: a library row nothing uses, or a section that quietly grew an item.
 */
export const LabSlideOver: React.FC<{
  open: boolean;
  title: string;
  subtitle?: React.ReactNode;
  /** Where "attach" would put it, named. Omit and the toggle is not offered at all. */
  attachTarget?: string;
  destination?: LabSaveDestination;
  onDestinationChange?: (destination: LabSaveDestination) => void;
  saveLabel?: string;
  saveDisabled?: boolean;
  /** The reasons it is disabled, listed under the button rather than left to be guessed. */
  saveBlockers?: string[];
  saving?: boolean;
  onSave: () => void;
  onClose: () => void;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  children: React.ReactNode;
}> = ({
  open,
  title,
  subtitle,
  attachTarget,
  destination = 'attach',
  onDestinationChange,
  saveLabel = 'Save',
  saveDisabled = false,
  saveBlockers = [],
  saving = false,
  onSave,
  onClose,
  size = 'large',
  children,
}) => (
  <HbrDrawer
    open={open}
    placement="right"
    size={size}
    label={title}
    noHeader
    noPadding
    onHbr-hide={onClose}
  >
    <div className="flex h-full flex-col">
      <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
        <div>
          <h3 className="m-0 text-base font-semibold">{title}</h3>
          {subtitle && <div className="mt-1 text-xs text-gray-500">{subtitle}</div>}
        </div>
        <HbrIcon
          asset={x}
          label="Close"
          className="cursor-pointer p-1 text-xl opacity-60 hover:opacity-100"
          onClick={onClose}
        />
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>

      <footer className="border-t border-gray-200 px-4 py-3">
        {attachTarget && onDestinationChange && (
          <div className="mb-3 flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              When saved
            </span>
            <div className="flex flex-wrap gap-2">
              {[
                {
                  value: 'attach' as LabSaveDestination,
                  label: `Save and attach to ${attachTarget}`,
                },
                { value: 'library' as LabSaveDestination, label: 'Save to the library only' },
              ].map((choice) => (
                <button
                  key={choice.value}
                  type="button"
                  className={`rounded-full border px-3 py-1 text-xs ${
                    destination === choice.value
                      ? 'border-blue-600 bg-blue-50 text-blue-800'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => onDestinationChange(choice.value)}
                >
                  {choice.label}
                </button>
              ))}
            </div>
            {destination === 'library' && (
              <span className="text-xs text-gray-500">
                It will exist in the library and nothing will use it until something attaches it.
              </span>
            )}
          </div>
        )}

        {!!saveBlockers.length && (
          <ul className="mb-2 mt-0 list-disc pl-5 text-xs text-amber-800">
            {saveBlockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <StbButton role="cancel" size="small" onClick={async () => onClose()}>
            Cancel
          </StbButton>
          <StbButton
            role="save"
            size="small"
            disabled={saveDisabled}
            loading={saving}
            onClick={async () => onSave()}
          >
            {saveLabel}
          </StbButton>
        </div>
      </footer>
    </div>
  </HbrDrawer>
);

/* ------------------------------------------------------------------------- *
 * Sticky save bar
 * ------------------------------------------------------------------------- */

/**
 * Save, always reachable.
 *
 * The item editor and the lab info panel are both long enough to scroll, and a save button that
 * has scrolled off is how a form gets abandoned with the work still in it. Only rendered when
 * there is something to save, so it does not sit there claiming a clean form is dirty.
 */
export const LabStickySaveBar: React.FC<{
  dirty: boolean;
  saving?: boolean;
  saveLabel?: string;
  saveDisabled?: boolean;
  blockers?: string[];
  onSave: () => void;
  onDiscard?: () => void;
}> = ({
  dirty,
  saving = false,
  saveLabel = 'Save',
  saveDisabled = false,
  blockers = [],
  onSave,
  onDiscard,
}) => {
  if (!dirty) return null;

  return (
    <HbrStickyActionBar sticky raised align="end" size="sm">
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-gray-600">
          {blockers.length ? (
            <span className="text-amber-800">{blockers.join(' ')}</span>
          ) : (
            'Unsaved changes.'
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {onDiscard && (
            <StbButton role="cancel" size="small" onClick={async () => onDiscard()}>
              Discard
            </StbButton>
          )}
          <StbButton
            role="save"
            size="small"
            disabled={saveDisabled}
            loading={saving}
            onClick={async () => onSave()}
          >
            {saveLabel}
          </StbButton>
        </div>
      </div>
    </HbrStickyActionBar>
  );
};

/* ------------------------------------------------------------------------- *
 * Toasts
 * ------------------------------------------------------------------------- */

/**
 * Where confirmations appear.
 *
 * Mounted by the admin page rather than by the app shell, so nothing outside lab administration
 * gains a toast host it did not ask for. A modal for "saved" would need dismissing, which puts a
 * click between the admin and the next of the six edits they came to make; a toast does not.
 */
export const LabToastHost: React.FC = () => (
  <Toaster
    position="bottom-right"
    toastOptions={{
      duration: 4000,
      // Errors stay until dismissed. A failure that vanishes on its own is a failure nobody saw,
      // and every one of these is a write that did not happen.
      error: { duration: Infinity },
      style: { fontSize: '0.875rem', maxWidth: '32rem' },
    }}
  />
);

/**
 * Say what happened, without stopping.
 *
 * `saved` names the row, because "Saved" alone is ambiguous the moment two panels are open. The
 * error variant takes the thrown value rather than a string so callers cannot forget to unwrap it
 * and show "[object Object]".
 */
export const useLabToasts = () => {
  const saved = useCallback((what: string) => toast.success(`Saved ${what}.`), []);
  const removed = useCallback((what: string) => toast.success(`Deleted ${what}.`), []);
  const detached = useCallback(
    (what: string, from: string) =>
      toast.success(`Detached ${what} from ${from}. It is still in the library.`),
    [],
  );
  const failed = useCallback(
    (what: string, error: unknown) =>
      toast.error(`Could not ${what}: ${(error as Error)?.message || 'the API refused it.'}`),
    [],
  );
  const said = useCallback((message: string) => toast(message), []);

  return useMemo(
    () => ({ saved, removed, detached, failed, said }),
    [saved, removed, detached, failed, said],
  );
};

/* ------------------------------------------------------------------------- *
 * Chips, search, facets, bulk actions
 * ------------------------------------------------------------------------- */

/**
 * One selected value, removable.
 *
 * Used for prerequisites, which are a list of rows rather than a piece of text: a chip shows the
 * whole selection at once and each entry carries its own remove, which is what makes a
 * seven-prerequisite item readable at all.
 */
export const LabChip: React.FC<{
  label: string;
  hint?: string;
  onRemove?: () => void;
  /** Marks a chip that is there because something else needed it, not because it was chosen. */
  implied?: boolean;
}> = ({ label, hint, onRemove, implied = false }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
      implied ? 'border-dashed border-gray-400 bg-gray-50 text-gray-700' : 'border-gray-300 bg-white'
    }`}
    title={hint}
  >
    {label}
    {implied && <span className="text-gray-500">(pulled in)</span>}
    {onRemove && (
      <button
        type="button"
        className="ml-0.5 border-0 bg-transparent p-0 leading-none text-gray-500 hover:text-red-700"
        aria-label={`Remove ${label}`}
        onClick={onRemove}
      >
        <HbrIcon asset={x} className="text-sm" />
      </button>
    )}
  </span>
);

/** The search box every library view has, in the same place, doing the same thing. */
export const LabSearchBox: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, placeholder = 'Search', className = '' }) => (
  <HbrInput
    className={className}
    size="small"
    type="search"
    clearable
    value={value}
    placeholder={placeholder}
    arialabel={placeholder}
    onHbr-input={(evt: any) => onChange(evt.target.value ?? '')}
    onHbr-clear={() => onChange('')}
  >
    <HbrIcon asset={magnifyingGlass} slot="prefix" />
  </HbrInput>
);

/**
 * One filter over a facet the rows already have.
 *
 * Facets, not tags: none of these tables has a tags column, and the useful narrowings are the
 * columns that already exist - what operation an item performs, what kind of device it is written
 * for, which data centre a lab reaches. A tags field would be a second, hand-maintained copy of
 * facts the rows already state.
 */
export function LabFacetFilter<T extends string>({
  label,
  value,
  options,
  onChange,
  className = '',
}: {
  label: string;
  value: T | '';
  options: LabOption<T>[];
  onChange: (value: T | '') => void;
  className?: string;
}) {
  return (
    <label className={`flex items-center gap-2 text-xs text-gray-600 ${className}`}>
      <span className="whitespace-nowrap">{label}</span>
      <select
        className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
        value={value}
        onChange={(evt) => onChange(evt.target.value as T | '')}
      >
        <option value="">Any</option>
        {options.map((option) => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * What can be done to the rows that are ticked.
 *
 * Only rendered when something is ticked, and it says how many - a bulk bar that is always
 * present invites a click before the selection is read, and "Delete" over an unread selection is
 * the one mistake here that cannot be undone.
 */
export const LabBulkBar: React.FC<{
  count: number;
  actions: LabRowAction[];
  onClear: () => void;
}> = ({ count, actions, onClear }) => {
  if (!count) return null;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded border border-blue-200 bg-blue-50 px-3 py-2">
      <span className="text-sm font-medium text-blue-900">
        {count} selected
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {actions.map((action) => (
          <StbButton
            key={action.key}
            role={action.destructive ? 'delete' : 'default'}
            size="xsmall"
            disabled={action.disabled}
            onClick={async () => action.onSelect()}
          >
            {action.label}
          </StbButton>
        ))}
      </div>
      <button
        type="button"
        className="ml-auto border-0 bg-transparent p-0 text-xs text-blue-800 underline"
        onClick={onClear}
      >
        Clear selection
      </button>
    </div>
  );
};

/* ------------------------------------------------------------------------- *
 * Tree affordances
 * ------------------------------------------------------------------------- */

/** The grip. Presentational - the row above it owns the drag, this only says where to grab. */
export const LabDragHandle: React.FC<{ label?: string; className?: string }> = ({
  label = 'Drag to reorder',
  className = '',
}) => (
  <HbrIcon
    asset={dotsSix}
    label={label}
    title={label}
    className={`cursor-grab text-base text-gray-400 ${className}`}
  />
);

/**
 * That this row is not free to move, and what is holding it.
 *
 * Order and prerequisites can disagree, and when they do the prerequisites win - so a row whose
 * position looks wrong may be exactly where it has to be. The count and the names are both here
 * because the count alone prompts the question this is meant to answer.
 */
export const LabDependencyMark: React.FC<{ names: string[]; className?: string }> = ({
  names,
  className = '',
}) => {
  if (!names.length) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs text-gray-500 ${className}`}
      title={`Runs after: ${names.join(', ')}`}
    >
      <HbrIcon asset={treeView} className="text-sm" />
      {names.length}
    </span>
  );
};

/**
 * An expand/collapse caret that knows whether there is anything to expand.
 *
 * An empty section still gets a caret, disabled, rather than losing it: the row would otherwise
 * be indented differently from its siblings, which reads as a different kind of row.
 */
export const LabTwisty: React.FC<{
  open: boolean;
  empty?: boolean;
  onToggle: () => void;
  label?: string;
}> = ({ open, empty = false, onToggle, label = 'Expand' }) => (
  <button
    type="button"
    className={`flex h-5 w-5 items-center justify-center border-0 bg-transparent p-0 ${
      empty ? 'opacity-25' : 'text-gray-500 hover:text-gray-900'
    }`}
    aria-expanded={open}
    aria-label={label}
    disabled={empty}
    onClick={onToggle}
  >
    <HbrIcon asset={open ? caretDown : caretRight} className="text-base" />
  </button>
);

/* ------------------------------------------------------------------------- *
 * Highlighting a node the tree has just gained
 * ------------------------------------------------------------------------- */

/**
 * Which node was just created, for as long as it takes to notice it.
 *
 * The slide-over closes and the tree grows a row. Without this the row is somewhere among twelve
 * siblings and finding it is a small search every single time - which is exactly the friction the
 * slide-over exists to remove. It clears itself, because a highlight that persists becomes a
 * selection, and there is already one of those.
 */
export const useLabFlash = (durationMs = 2500) => {
  const [flashed, setFlashed] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const flash = useCallback(
    (key: string) => {
      if (timer.current) clearTimeout(timer.current);
      setFlashed(key);
      timer.current = setTimeout(() => setFlashed(null), durationMs);
    },
    [durationMs],
  );

  return { flashed, flash };
};
